// T-190 (v1.8.64, Session 46, 09.10.2026): kompakte Terminliste "Nächste Termine" für die Website (Kundin 06.10.2026 Punkt 4,
// Zusage Mail 07.10.2026 Punkt 4b). Fasst die Termine wie der Gästekalender je Datum und Uhrzeit zusammen (alle Tourarten zur
// selben Uhrzeit teilen sich die Plätze), ohne vergangene, ausgebuchte und gesperrte Termine; Termine in Planung mit
// "ab n Personen buchbar" (T-181). Reine Funktionen, getestet in naechsteTermine.test.ts.
import { mindestPersonenGruppe } from './kalenderAnfrage';
import { zeitKurz } from './terminLink';

/** Felder eines Kalendertermins (GET api/calendar/slots), soweit die Liste sie braucht. */
export interface TerminSlot {
  date: string;
  time: string;
  tourType: string;
  availableCapacity: number;
  isAvailable: boolean;
  isBlocked: boolean;
  isReleasedForOnlineBooking?: boolean;
  minCapacity?: number | null;
  minParticipants?: number | null;
}

/** Eine Zeile der Liste. */
export interface Termineintrag {
  datum: string;
  zeit: string;
  touren: string[];
  frei: number;
  geplant: boolean;
  mindestPersonen: number;
}

export const STANDARD_ANZAHL = 6;
export const MAX_ANZAHL = 20;
/** Ab dieser Zahl freier Plätze oder weniger heißt es "nur noch". */
export const KNAPP_AB = 20;

/** Anzahl der Zeilen aus dem Parameter anzahl: ganze Zahl 1 bis 20, sonst 6. */
export function anzahlAusParameter(wert: string | null | undefined): number {
  if (wert === null || wert === undefined || wert.trim() === '') return STANDARD_ANZAHL;
  const n = Number(wert);
  return Number.isInteger(n) && n >= 1 && n <= MAX_ANZAHL ? n : STANDARD_ANZAHL;
}

/** true, wenn Datum und Uhrzeit vor jetzt liegen (Ortszeit des Browsers, wie im Gästekalender). */
export function istVergangen(datum: string, zeit: string, jetzt: Date): boolean {
  const [j, m, t] = datum.split('-').map(Number);
  const [h, mi] = zeitKurz(zeit).split(':').map(Number);
  const beginn = new Date(j, (m || 1) - 1, t || 1, h || 0, mi || 0, 0, 0);
  return beginn.getTime() < jetzt.getTime();
}

const buchbar = (s: TerminSlot): boolean =>
  s.isReleasedForOnlineBooking !== false && s.isAvailable && !s.isBlocked && (s.availableCapacity ?? 0) > 0;

/**
 * Die nächsten Termine ab jetzt, höchstens anzahl Zeilen, sortiert nach Datum und Uhrzeit. tour: nur diese Tourart (Code,
 * ohne Beachtung der Groß- und Kleinschreibung). Freigegebene Zeitgruppen erscheinen nur mit freien Plätzen (Zahl wie im
 * Gästekalender: die meisten freien Plätze der buchbaren Tourarten), Termine in Planung nur, wenn nicht alle gesperrt sind.
 */
export function naechsteTermine(
  slots: ReadonlyArray<TerminSlot | null | undefined> | null | undefined,
  jetzt: Date,
  anzahl: number = STANDARD_ANZAHL,
  tour?: string | null,
): Termineintrag[] {
  const filter = (tour ?? '').trim().toLowerCase();
  const gruppen = new Map<string, TerminSlot[]>();
  for (const s of slots ?? []) {
    if (!s || typeof s.date !== 'string' || typeof s.time !== 'string') continue;
    if (filter && (s.tourType ?? '').trim().toLowerCase() !== filter) continue;
    const schluessel = `${s.date}|${zeitKurz(s.time)}`;
    const liste = gruppen.get(schluessel);
    if (liste) liste.push(s);
    else gruppen.set(schluessel, [s]);
  }

  const eintraege: Termineintrag[] = [];
  gruppen.forEach((gruppe, schluessel) => {
    const [datum, zeit] = schluessel.split('|');
    if (istVergangen(datum, zeit, jetzt)) return;
    const geplant = gruppe.every(s => s.isReleasedForOnlineBooking === false);
    let frei: number;
    if (geplant) {
      if (gruppe.every(s => s.isBlocked)) return;
      frei = Math.max(0, ...gruppe.map(s => s.availableCapacity ?? 0));
    } else {
      const offen = gruppe.filter(buchbar);
      if (offen.length === 0) return;
      frei = Math.max(...offen.map(s => s.availableCapacity));
    }
    const touren = Array.from(new Set(gruppe.map(s => (s.tourType ?? '').trim()).filter(t => t !== ''))).sort((a, b) => a.localeCompare(b));
    eintraege.push({ datum, zeit, touren, frei, geplant, mindestPersonen: mindestPersonenGruppe(gruppe) });
  });

  eintraege.sort((a, b) => (a.datum === b.datum ? a.zeit.localeCompare(b.zeit) : a.datum.localeCompare(b.datum)));
  const grenze = Number.isInteger(anzahl) && anzahl >= 1 ? Math.min(anzahl, MAX_ANZAHL) : STANDARD_ANZAHL;
  return eintraege.slice(0, grenze);
}

/** "noch 120 Plätze frei", bei 20 oder weniger "nur noch 12 Plätze frei" bzw. "nur noch 1 Platz frei". */
export function platzText(frei: number): string {
  const n = Math.max(0, Math.floor(frei));
  const wort = n === 1 ? 'Platz' : 'Plätze';
  return n <= KNAPP_AB ? `nur noch ${n} ${wort} frei` : `noch ${n} ${wort} frei`;
}

const WOCHENTAGE = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
const zweistellig = (n: number): string => String(n).padStart(2, '0');

/** "Sa., 24.10.2026" (ohne date-fns/locale, das Jest in dieser Umgebung nicht laden kann) */
export function datumText(datum: string): string {
  const [j, m, t] = datum.split('-').map(Number);
  const d = new Date(j, m - 1, t);
  return `${WOCHENTAGE[d.getDay()]}, ${zweistellig(t)}.${zweistellig(m)}.${j}`;
}

/** Datum im Format JJJJ-MM-TT nach Ortszeit des Browsers. */
export function datumSchluessel(d: Date): string {
  return `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;
}

/** Datum plus n Monate (Monatsende wird auf den letzten Tag begrenzt, z. B. 31.01. plus 1 Monat = 28.02.). */
export function plusMonate(d: Date, n: number): Date {
  const ziel = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const letzter = new Date(ziel.getFullYear(), ziel.getMonth() + 1, 0).getDate();
  return new Date(ziel.getFullYear(), ziel.getMonth(), Math.min(d.getDate(), letzter));
}

/** Tournamen einer Zeile aus der Tourenliste (Code zu Name), unbekannte Codes bleiben stehen; mehrere mit " oder ". */
export function tourText(touren: ReadonlyArray<string>, namen: Readonly<Record<string, string>>): string {
  return touren.map(t => (namen[t] && namen[t].trim() !== '' ? namen[t].trim() : t)).join(' oder ');
}
